import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
import type { Recipient } from '../types/disclosures';
interface EditRecipientModalProps {
  recipient: Recipient;
  onClose: () => void;
  onSave: (recipient: Recipient) => void;
}
export function EditRecipientModal({
  recipient,
  onClose,
  onSave
}: EditRecipientModalProps) {
  const [draft, setDraft] = useState<Recipient>(recipient);
  useEffect(() => {
    setDraft(recipient);
  }, [recipient]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSave(draft);
  };
  return <div className="fixed inset-0 z-40 flex items-center justify-center p-4" data-id="element-106" data-mp="kdcrk59lffi2">
      <div className="absolute inset-0 bg-slate-900/25 backdrop-blur-[1px]" onClick={onClose} aria-hidden="true" data-id="element-107" data-mp="ffb7pi4cfc5j" />
      <div role="dialog" aria-modal="true" aria-labelledby="edit-recipient-title" className="relative w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-pop" data-id="element-108" data-mp="34m5mvugh8k6g">
        <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4" data-id="element-109" data-mp="2vlpjekzqhd1l">
          <div data-id="element-110" data-mp="30kamqtaa8cu9">
            <h2 id="edit-recipient-title" className="text-[15px] font-semibold text-slate-900" data-id="element-111" data-mp="3o8adht7lg38u">
              Edit Recipient
            </h2>
            <p className="mt-0.5 text-[13px] text-slate-500" data-id="element-112" data-mp="3s8c6ldz9joci">{draft.role}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label="Close" data-id="element-113" data-mp="3eh5vslqja4ab">
            <XIcon className="h-4 w-4" data-id="element-114" data-mp="fta6gn3lvvdm" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4" data-id="element-115" data-mp="pnxis7z5ixf7">
          <Field label="Full name" data-id="element-116" data-mp="pm4kph2ltlit">
            <input type="text" value={draft.name} onChange={event => setDraft({
            ...draft,
            name: event.target.value
          })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-[14px] text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/5" data-id="element-117" data-mp="kol0m0j86eiy" />
          </Field>
          <Field label="Email address" data-id="element-118" data-mp="1ymsxljcanfu">
            <input type="email" value={draft.email} onChange={event => setDraft({
            ...draft,
            email: event.target.value
          })} placeholder="name@email.com" className="w-full rounded-md border border-slate-200 px-3 py-2 text-[14px] text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/5" data-id="element-119" data-mp="1xld73rdpcrs" />
          </Field>
          <Field label="Phone" data-id="element-120" data-mp="3f5y9mm7vdvgt">
            <input type="tel" value={draft.phone} onChange={event => setDraft({
            ...draft,
            phone: event.target.value
          })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-[14px] text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/5" data-id="element-121" data-mp="1un46l7d5z6yg" />
          </Field>
          <Field label="Delivery preference" data-id="element-122" data-mp="28kss8hf22i4k">
            <select value={draft.delivery} onChange={event => setDraft({
            ...draft,
            delivery: event.target.value as Recipient['delivery']
          })} className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/5" data-id="element-123" data-mp="1azwg4b6qyb40">
              <option value="Electronic" data-id="element-124" data-mp="s7lurv84yvtn">Electronic</option>
              <option value="Paper" data-id="element-125" data-mp="x7qdlrwio2n1">Paper</option>
            </select>
          </Field>

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4" data-id="element-126" data-mp="x5dlilx1obw9">
            <button type="button" onClick={onClose} className="rounded-md px-3 py-2 text-[13px] font-medium text-slate-600 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-127" data-mp="2yx5mb6isxjc3">
              Cancel
            </button>
            <button type="submit" className="rounded-md bg-slate-900 px-3.5 py-2 text-[13px] font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2" data-id="element-128" data-mp="33u5rzgbqok2o">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>;
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <label className="block" data-id="element-129" data-mp="1x9mqxnrw0zpy">
      <span className="mb-1.5 block text-[12px] font-medium text-slate-600" data-id="element-130" data-mp="1s8pb3hucohrm">{label}</span>
      {children}
    </label>;
}